import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgFundraisersListOrgFundraisersQueryKey } from '@givmo/api-client/query';
import {
  firstErrorCode,
  isGivmoError,
  sdk,
  type OrgFundraiserOut,
  type OrgFundraiserUpdate,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Icon,
  PageHeader,
  Switch,
  Tabs,
  TextField,
  Textarea,
  cn,
  saasButtonVariants,
  shouldInterceptClick,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { ArrowLeft, CalendarClock, EyeOff, HelpCircle, Lock, ShieldAlert } from 'lucide-react';
import {
  BOARD_ASK_COPY,
  ShareKit,
  boardAskKeys,
  fetchBoardAskForCampaign,
} from '@givmo/org-console';
import { ConfirmDialog } from './confirm-dialog';
import { useFundraisersModule } from './fundraisers-context';
import { mayReadFundraisers, mayWriteFundraisers } from './fundraisers-config';
import {
  fundraiserOptions,
  privateFundraiserOptions,
  requireOrgFundraiser,
} from './fundraisers-queries';
import { campaignOptions, TEAMS_OFF } from './campaign-queries';
import { FUNDRAISERS_ROUTES } from './fundraisers-routes';
import {
  FundraisersLoading,
  FundraisersPageShell,
  FundraisersReadState,
} from './fundraisers-state';
import {
  buildCreateBody,
  buildUpdatePatch,
  displayGoal,
  emptyForm,
  fundraiserSchema,
  fundraiserToForm,
  type FundraiserFormValues,
} from './fundraiser-form';
import {
  LIFECYCLE_COPY,
  LIFECYCLE_TOASTS,
  formatFundraiserMoney,
  fundraiserBadge,
  fundraiserEndings,
  lifecycleRefusal,
  takedownQuestionHref,
  takedownSupportHref,
} from './fundraisers-decode';
import { isCampaign } from './fundraisers-page';
import { GoalThermometer } from './goal-thermometer';
import { FundraiserCover } from './fundraiser-cover';
import { COVER_FRAME_CLASS } from './cover';
import { FundraiserCreationWizard } from './creation-wizard';
import { DesignationsEditor } from './designations-editor';
import { RecipientPicker } from './recipient-picker';
import { currentRecipients } from './recipients-decode';
import { OrgContactOffer } from './org-contact-offer';
import {
  contactOfferFromOrgRead,
  orgContactProblem,
  orgContactRefusal,
  orgContactWrite,
} from './org-contact-offer-decode';
import { SharePanel } from './share-panel';
import { P2PCampaignPage } from './p2p-campaign-page';
import { LayersTab, LeaveDialogHost, type KeptBy } from './layers-tab';
import { DonorsTab, DonorsUnavailable, donorListServed } from './donors-tab';
import { DONORS_COPY } from './donors-copy';
import { MessagesTab, MessagesUnavailable } from './messages-tab';
import { EMAILS_COPY } from './emails-copy';
import { useSenderIdentity } from './sender-identity';
import { EmailsTab, EmailsUnavailable } from './emails-tab';
import { CAMPAIGN_SOCIAL_COPY } from './campaign-social-copy';
import { isCampaignSocialUnavailable } from './campaign-social-decode';
import { SHARE_GRAPHICS_CARD_ID, ShareGraphicsHint } from './campaign-share-graphics';
import {
  CampaignSocialTab,
  CampaignSocialUnavailable,
  postsBesideComposer,
  useCampaignSocial,
} from './campaign-social-tab';
import { MESSAGES_COPY } from './messages-copy';
import { messagesServed } from './messages-decode';
import { TabCount } from './join-requests';
import { LAYERS_COPY as LC, SERVER_WORDS } from './layers-copy';
import { endedFromAbove, formatEndDay } from './layers-decode';
import {
  invalidateLayerReads,
  layerParentOptions,
  layerStructureOptions,
  publicCampaignOptions,
} from './layers-queries';
import { BonusPledgesTab } from './bonus-pledges-tab';
import { BONUS_COPY } from './bonus-pledges-copy';
import { isGameRead } from './bonus-pledges-decode';
import { useLivePage } from './use-live-page';
import {
  EmptyValue,
  FootButton,
  FootCard,
  INLINE_HIT,
  PHONE_HIT,
  RecordRow,
  StateNotice,
} from './lifecycle-ui';

const EDITOR_COLUMNS = 'grid grid-cols-1 items-start gap-24 xl:grid-cols-[minmax(0,1fr)_300px]';
/** The Social tab with its posts beside Social's composer (frame 40): the tab lays out both columns. */
const ONE_COLUMN = 'grid grid-cols-1 items-start gap-24';

const CAMPAIGN_EXPLAINER =
  'A Campaign is a fundraiser that brings together many fundraiser pages. People can create their own pages and raise toward the campaign’s goal.';
// Existing enable disclosure, retained at the action that makes these reads public.
const PUBLIC_ENABLE_COPY =
  'Fundraiser pages, team leaderboards, and campaign fundraising totals become PUBLIC on P2P pages once enabled.';

// Plan §FC-15 (D-FCM-16): closing is reversible. "stops all new donations" is pinned by the e2e
// charity spec's close segment. With the totals hidden the page shows no final total, so the dialog says
// what it does show (Jonathan's app mockup ruling A3, 2026-09-27; FCM-WEB24).
const CLOSE_COPY =
  'Closing stops all new donations. The page keeps showing its final total, and you can reopen it at any time.';
const CLOSE_COPY_TOTALS_HIDDEN =
  'Closing stops all new donations. The page stays up, and you can reopen it at any time.';
const REOPEN_COPY = 'It will accept donations again.';

/**
 * The server's own explanation when a closed row cannot be reopened (409
 * `FUNDRAISER_NOT_REOPENABLE`), or `null` for any other refusal. Keyed on the typed code, never the
 * status or the English. On the `/org` door this module calls, only a `group_dissolved` close is
 * refused, and defensively so: an organization's fundraiser is never group-owned. An
 * `account_deleted` close reopens there, since the organization outlives its admin (backend
 * `OrgFundraiserService.reopen_fundraiser`).
 */
function notReopenableReason(error: unknown): string | null {
  return isGivmoError(error) && firstErrorCode(error) === 'FUNDRAISER_NOT_REOPENABLE'
    ? error.message
    : null;
}

/**
 * FCM-WEB28 (D-FCM-24): the `/org` door's other ways to end a fundraiser, or to undo an ending
 * (FCM-BE39) — beside Publish, Close and Reopen, which keep their own mutation.
 */
type EndingAct = 'deactivate' | 'reactivate' | 'deleteDraft' | 'deleteForever';

export function FundraiserCreatePage() {
  const config = useFundraisersModule();
  // EMC-WEB7: behind the host's knob, a writable organization's New fundraiser is the creation
  // wizard. While the organization resolves, or for a person who can't write, the page says so as the
  // one-page form's page always has.
  return config.creationWizard === true && mayWriteFundraisers(config) ? (
    <FundraiserCreationWizard key={config.orgRef} />
  ) : (
    <CreateDraft key={config.orgRef ?? 'pending'} />
  );
}

function CreateDraft() {
  const config = useFundraisersModule();
  const client = useQueryClient();
  const live = useLivePage();
  const writable = mayWriteFundraisers(config);
  return (
    <FundraisersPageShell testId="page-fundraiser-editor">
      <BackButton onClick={() => config.onNavigate(FUNDRAISERS_ROUTES.list)} />
      <PageHeader title="New fundraiser" subtitle={config.flavor.createDescription} />
      {!config.orgRef ? (
        <FundraisersLoading />
      ) : !writable ? (
        <FundraisersReadState denied onRetry={() => undefined} />
      ) : (
        <div className="grid grid-cols-1 items-start gap-24 xl:grid-cols-[minmax(0,1fr)_300px]">
          <FundraiserFormCard
            canEditContent
            canChangeStatistics
            onSaved={async (row) => {
              await client.invalidateQueries({
                queryKey: orgFundraisersListOrgFundraisersQueryKey({
                  path: { org_ref: config.orgRef! },
                }),
              });
              if (live()) config.onNavigate(FUNDRAISERS_ROUTES.editor(row.id));
            }}
          />
          <Card flush testId="fundraiser-create-hint-card">
            <CardHeader title="Start with a draft" />
            <CardBody>
              <p className="text-s14 text-text-secondary">
                Create the draft, then add a cover photo
                {config.flavor.recipientMode === 'chosen' ? ' and choose recipients' : ''}. Publish
                when it’s ready to share.
              </p>
            </CardBody>
          </Card>
        </div>
      )}
    </FundraisersPageShell>
  );
}

export function FundraiserEditorPage({
  fundraiserId,
  view = 'details',
}: {
  fundraiserId: string;
  view?:
    | 'details'
    | 'donors'
    | 'emails'
    | 'social'
    | 'messages'
    | 'bonus-pledges'
    | 'campaign'
    | 'layers';
}) {
  const config = useFundraisersModule();
  return (
    <LoadedEditor
      key={`${config.orgRef ?? 'pending'}:${fundraiserId}`}
      fundraiserId={fundraiserId}
      view={view}
    />
  );
}

function LoadedEditor({
  fundraiserId,
  view,
}: {
  fundraiserId: string;
  view:
    | 'details'
    | 'donors'
    | 'emails'
    | 'social'
    | 'messages'
    | 'bonus-pledges'
    | 'campaign'
    | 'layers';
}) {
  const config = useFundraisersModule();
  const client = useQueryClient();
  const live = useLivePage();
  const readable = mayReadFundraisers(config);
  const options = fundraiserOptions(config.orgRef ?? '', fundraiserId);
  const query = useQuery({ ...options, enabled: readable });
  const scoped = query.isSuccess && query.data != null;
  const privateOptions = privateFundraiserOptions(fundraiserId);
  const privateQuery = useQuery({ ...privateOptions, enabled: scoped });
  const umbrellaOptions = campaignOptions(fundraiserId);
  const umbrella = useQuery({ ...umbrellaOptions, enabled: scoped, retry: false });
  // EMC-WEB1/WEB3: the sending-domain read decides whether the Emails tab shows. Read once the detail
  // is this organization's, never before; its route miss (email campaigns dark here) answers `null`.
  const senderIdentity = useSenderIdentity(scoped ? config.orgRef : null);
  // EMC-WEB5: the campaign's Social posts decide whether the Social tab shows: only where the host mounts
  // Social and the read says Social is offered to the organization and on (a route miss is no Social).
  const socialPosts = useCampaignSocial(
    config.orgRef,
    fundraiserId,
    scoped && config.social != null,
  );
  // FCM-WEB25: where the campaign sits and what its Close reaches, while layers serve (the overview
  // carries its `layers` rows only then; every layer read is otherwise the absent route).
  const layersOn = umbrella.isSuccess && umbrella.data.layers !== null;
  const parentQuery = useQuery({
    ...layerParentOptions(fundraiserId),
    enabled: layersOn,
    retry: false,
  });
  const structureQuery = useQuery({
    ...layerStructureOptions(fundraiserId),
    enabled: layersOn,
    retry: false,
  });
  // FCM-WEB35 (D-FCM-77): a game's page has a Bonus pledges tab and calls itself a game (production fix 14).
  // A game is a campaign with a scoreboard of its own (its public read's `scoreboard`, FCM-BE118), the mark
  // the server's bonus pledge act takes. The read is the Layers tab's own, so it costs no request more.
  const gameRead = useQuery({
    ...publicCampaignOptions(fundraiserId),
    enabled: layersOn,
    retry: false,
  });
  const isGame = isGameRead(gameRead.data);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [help, setHelp] = useState(false);
  const [dialog, setDialog] = useState<
    | 'publish'
    | 'close'
    | 'reopen'
    | 'upgrade'
    | 'deactivate'
    | 'deleteDraft'
    | 'deleteForever'
    | null
  >(null);
  // The server's explanation once a Reopen was refused as not reopenable: the org read carries no
  // close reason, so the refusal is the only place this page can learn it.
  const [notReopenable, setNotReopenable] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [leave, setLeave] = useState<string | null>(null);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  function navigate(href: string) {
    if (dirty) setLeave(href);
    else config.onNavigate(href);
  }
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: options.queryKey }),
      client.invalidateQueries({ queryKey: privateOptions.queryKey }),
      client.invalidateQueries({ queryKey: umbrellaOptions.queryKey }),
      invalidateLayerReads(client),
      client.invalidateQueries({
        queryKey: orgFundraisersListOrgFundraisersQueryKey({
          path: { org_ref: config.orgRef ?? '' },
        }),
      }),
    ]);
  };
  const row = query.data;
  const writable = mayWriteFundraisers(config);
  const effectiveRole = row?.effective_fundraiser_role;
  const editorRank = effectiveRole === 'OWNER' || effectiveRole === 'EDITOR';
  const ownerRank = effectiveRole === 'OWNER';
  // FCM-WEB28: every way to end a fundraiser, or to undo an ending, shows exactly when the record's
  // `lifecycle` hint says the `/org` door would accept it (FCM-BE39); a backend without hints keeps the
  // page's own Close and Reopen rules and offers nothing more.
  const endings = row ? fundraiserEndings(row) : null;
  const takenDown = endings?.takenDown === true;
  // The organization's own deactivation. Only the hints tell it from a Givmo takedown, so without them
  // (a backend before FCM-BE39) a deactivated page stays read-only, as it was before FCM-WEB28.
  const ownDeactivation = row?.state === 'deactivated' && endings?.hinted === true && !takenDown;
  // "Create Board Ask" is on the campaign once the backend serves it: while its flag is off the
  // read is the route miss and the button is absent, never a dead end. The composer reads the same.
  const boardAskRead = useQuery({
    queryKey: boardAskKeys.campaign(config.orgRef ?? '', fundraiserId),
    queryFn: ({ signal }) => fetchBoardAskForCampaign(config.orgRef ?? '', fundraiserId, signal),
    enabled: config.boardAsk === true && writable && config.orgRef !== null,
    retry: false,
  });
  const boardAskServed = boardAskRead.isSuccess;
  // What the server lets the organization edit (its `content_writable`): a draft, an open page, and
  // the organization's own deactivation (ruling 3, 2026-10-05) — unless a Close froze the page before
  // it came down. A Givmo takedown is read-only (ruling Q7).
  const contentEditable =
    writable &&
    (row?.state === 'draft' ||
      row?.state === 'active' ||
      (ownDeactivation && row?.closed_at == null));
  // A page a Close froze before it was deactivated (FCM-WEB28): the record, and when this person can
  // reactivate it, the approved lines say the way back (Reactivate, then Reopen).
  const frozenDeactivated = row?.state === 'deactivated' && !takenDown && row.closed_at != null;
  // The totals switch is a disclosure preference the server keeps on a closed or deactivated page.
  const canChangeStatistics =
    writable && (contentEditable || row?.state === 'closed' || ownDeactivation);
  // FCM-WEB25 (FCM-BE37): a campaign whose pages and layers pick has no charities of its own and
  // publishes without them (publish's one exception, plan §FC-16.1); it takes no gift directly (Q9).
  const wrapper = umbrella.data?.config.recipient_choice === 'supporters';
  const hasRecipients =
    config.flavor.recipientMode === 'self' ||
    wrapper ||
    (privateQuery.isSuccess && currentRecipients(privateQuery.data).length > 0);
  // FCM-BE35: a draft a campaign above ended keeps its state (the cascade stamps open rows only), and
  // the server refuses its publish and every change under it, so the page reads ended.
  const draftEnded =
    row?.state === 'draft' &&
    endedFromAbove(umbrella.data?.config, (parentQuery.data?.ancestors.length ?? 0) > 0);
  const canPublish = writable && row?.state === 'draft' && hasRecipients && !draftEnded;
  // FCM-WEB35 (frame 14): on a game's page the close is its owners' act, as its bonus pledges' acts are, so
  // someone who can only view them isn't offered it. The server's `/org` door still takes the write permission
  // (FCM-BE39): the owner-rank close the set proposed for a game's page is not built.
  const canClose = writable && endings?.close === true && (!isGame || ownerRank);
  const canReopen = writable && endings?.reopen === true && notReopenable === null;
  const endingAllowed: Record<EndingAct, boolean> = {
    deactivate: writable && endings?.deactivate === true,
    reactivate: writable && endings?.reactivate === true,
    deleteDraft: writable && endings?.deleteDraft === true,
    deleteForever: writable && endings?.deleteForever === true,
  };
  const canUpgrade =
    scoped &&
    ownerRank &&
    row?.stats.direct_raised == null &&
    !umbrella.isSuccess &&
    privateQuery.isSuccess &&
    privateQuery.data.can_upgrade_to_campaign === true;
  const campaignLoaded = umbrella.isSuccess && umbrella.data != null;
  const allowed = {
    publish: canPublish,
    close: canClose,
    reopen: canReopen,
    upgrade: canUpgrade,
  } as const;
  const lifecycle = useMutation({
    mutationFn: async (action: 'publish' | 'close' | 'reopen' | 'upgrade') => {
      if (!config.orgRef || dirty || !allowed[action])
        throw new Error('This change is not available.');
      if (action === 'upgrade')
        return sdk.campaignsEnableCampaign({
          path: { fundraiser_id: fundraiserId },
          body: TEAMS_OFF,
          throwOnError: true,
        });
      const opts = {
        path: { org_ref: config.orgRef, fundraiser_id: fundraiserId },
        throwOnError: true as const,
      };
      return action === 'publish'
        ? sdk.orgFundraisersPublishOrgFundraiser(opts)
        : action === 'close'
          ? sdk.orgFundraisersCloseOrgFundraiser(opts)
          : sdk.orgFundraisersReopenOrgFundraiser(opts);
    },
    onSuccess: async (_response, action) => {
      await refresh();
      if (!live()) return;
      setDialog(null);
      toast.success(
        action === 'publish'
          ? 'Fundraiser published'
          : action === 'close'
            ? LIFECYCLE_TOASTS.closed
            : action === 'reopen'
              ? LIFECYCLE_TOASTS.reopened
              : 'Campaign created',
      );
      if (action === 'upgrade') config.onNavigate(FUNDRAISERS_ROUTES.campaign(fundraiserId));
    },
    onError: (error, action) => {
      if (!live()) return;
      // A row that can never be reopened: the page's note takes the server's reason (a live region,
      // so it is announced; a toast would repeat it word for word) and the action retires, since it
      // could only be refused again.
      const reason = action === 'reopen' ? notReopenableReason(error) : null;
      if (reason !== null) {
        setNotReopenable(reason);
        setDialog(null);
        return;
      }
      toast.error(
        isGivmoError(error) ? error.message : 'Couldn’t update this fundraiser. Please try again.',
      );
    },
  });
  // FCM-WEB28: Deactivate, Reactivate, Delete draft and Delete forever, through the `/org` door's own
  // ops (FCM-BE39). A delete names the status the person saw (`expect_status`, FCM-BE28's
  // precondition), so a page gone stale can never delete a fundraiser that changed under it.
  const ending = useMutation({
    mutationFn: async (act: EndingAct) => {
      if (!config.orgRef || dirty || !endingAllowed[act])
        throw new Error('This change is not available.');
      const path = { org_ref: config.orgRef, fundraiser_id: fundraiserId };
      if (act === 'deactivate')
        await sdk.orgFundraisersDeactivateOrgFundraiser({ path, throwOnError: true });
      else if (act === 'reactivate')
        await sdk.orgFundraisersReactivateOrgFundraiser({ path, throwOnError: true });
      else
        await sdk.orgFundraisersDeleteOrgFundraiser({
          path,
          query: { expect_status: act === 'deleteDraft' ? 'Draft' : 'Deactivated' },
          throwOnError: true,
        });
      return act;
    },
    onSuccess: async (act) => {
      if (act === 'deleteDraft' || act === 'deleteForever') {
        await client.invalidateQueries({
          queryKey: orgFundraisersListOrgFundraisersQueryKey({
            path: { org_ref: config.orgRef ?? '' },
          }),
        });
        if (!live()) return;
        setDialog(null);
        toast.success(
          act === 'deleteDraft' ? LIFECYCLE_TOASTS.draftDeleted : LIFECYCLE_TOASTS.deleted,
        );
        config.onNavigate(FUNDRAISERS_ROUTES.list);
        return;
      }
      await refresh();
      if (!live()) return;
      setDialog(null);
      toast.success(
        act === 'deactivate' ? LIFECYCLE_TOASTS.deactivated : LIFECYCLE_TOASTS.reactivated,
      );
    },
    onError: (error) => {
      if (!live()) return;
      // A 409 means the fundraiser moved under the page (a first gift, a takedown, a change made
      // elsewhere), so no retry can succeed: the dialog closes, the server's own words show, and the
      // page re-reads so it offers only what the server now accepts (ruling 4). Any other refusal is
      // the module's ordinary one: its words, with the dialog left open to retry or cancel.
      const refusal = lifecycleRefusal(error, 'Couldn’t update this fundraiser. Please try again.');
      toast.error(refusal.message);
      if (refusal.stale) {
        setDialog(null);
        void refresh();
      }
    },
  });
  const busy = lifecycle.isPending || ending.isPending;

  if (!config.orgRef)
    return (
      <FundraisersPageShell testId="page-fundraiser-editor">
        <FundraisersLoading />
      </FundraisersPageShell>
    );
  if (!readable)
    return (
      <FundraisersPageShell testId="page-fundraiser-editor">
        <FundraisersReadState denied onRetry={() => void query.refetch()} />
      </FundraisersPageShell>
    );
  if (query.isPending)
    return (
      <FundraisersPageShell testId="page-fundraiser-editor">
        <FundraisersLoading />
      </FundraisersPageShell>
    );
  if (!readable || query.isError || !row)
    return (
      <FundraisersPageShell testId="page-fundraiser-editor">
        <FundraisersReadState
          denied={!readable}
          detail
          fundraiserId={fundraiserId}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      </FundraisersPageShell>
    );
  const upgraded = isCampaign(row);
  // EMC-WEB2: an upgraded row's fundraiser pages' share (`pages_raised`), served ahead of the
  // snapshot's regen and read tolerantly: absent, the panel reads as it did.
  const servedPages = (row.stats as { pages_raised?: unknown }).pages_raised;
  const pagesRaised = typeof servedPages === 'string' ? servedPages : null;
  // The Layers tab serves on a campaign while the backend serves layers (the overview's `layers` key).
  // Once the overview has answered otherwise (a plain fundraiser's 404, or no key while layers are
  // dark), its reads never run: it says so instead of loading forever.
  const layersServe = upgraded && layersOn;
  const layersUnavailable = !layersServe && umbrella.isFetched;
  // FCM-WEB49 (D-FCM-55): the Donors tab, while the detail lights the organizers' list (FCM-BE83).
  const donorsServe = donorListServed(row);
  // FCM-WEB50 (D-FCM-64): the Messages tab, while the detail lights the composer (FCM-BE84).
  const messagesServe = messagesServed(row);
  // EMC-WEB1/WEB3: no web knob. The backend's `EMAIL_CAMPAIGNS_ENABLED` serves the sending-domain read;
  // dark, it is the route miss the hook answers as `null`, and the tab is hidden and its own address
  // says emails aren't available (never "No emails yet"). A read that FAILED shows the tab, so the
  // card's own "Try again" is reachable. On its own address the tab shows while the read answers.
  const emailsDark = senderIdentity.isSuccess && senderIdentity.data == null;
  const emailsServe = senderIdentity.isError || senderIdentity.data != null;
  const emailsUnavailable = emailsDark;
  // EMC-WEB5: the Social tab, while Social serves here. A read that FAILED shows the tab, so its card's own
  // "Try again" is reachable; on its own address the tab shows while the read answers.
  const socialDark =
    config.social == null ||
    (socialPosts.isSuccess && !socialPosts.data.available) ||
    (socialPosts.isError && isCampaignSocialUnavailable(socialPosts.error));
  const socialServe =
    !socialDark && (socialPosts.isError || (socialPosts.isSuccess && socialPosts.data.available));
  // EMC-WEB5 (frame 40): once the campaign has posts and this person writes them, the posts stand beside
  // Social's composer in place of the side cards, and the tabs run the full width above both.
  const socialBeside =
    view === 'social' && !socialDark && postsBesideComposer(config.social, socialPosts.data);
  const columns = socialBeside ? ONE_COLUMN : EDITOR_COLUMNS;
  const badge = fundraiserBadge(row);
  // FCM-WEB25: the campaign it is part of (FCM-BE31's parent read) and the layers under it (FCM-BE33).
  const where = parentQuery.data ?? null;
  const parentRef = where?.ancestors[where.ancestors.length - 1] ?? null;
  const childLayers = umbrella.data?.layers ?? [];
  const linked = parentRef !== null || childLayers.length > 0;
  const layerConfig = umbrella.data?.config ?? null;
  const ownDonations = privateQuery.isSuccess
    ? (privateQuery.data.stats?.donation_count ?? 0)
    : null;
  // FCM-BE31's fold, option A, and FCM-BE88: a top whose funded layers keep it from being deleted says
  // why and shows the way out (removing those layers). Only the hints say it can't be deleted.
  const fundedLayers = childLayers.some((layer) => layer.donation_count > 0);
  const keptBy: KeptBy =
    endings?.hinted !== true || parentRef !== null || !fundedLayers
      ? null
      : row.state === 'draft' && !endings.deleteDraft
        ? 'draft'
        : row.state === 'deactivated' && !takenDown && !endings.deleteForever && ownDonations === 0
          ? 'deactivated'
          : null;
  // FCM-BE35: a close a campaign's end wrote, by its own deadline (frame 12) or one above it (12b, 07c).
  const closeReason = row.lifecycle?.close_reason ?? null;
  const campaignEnded =
    row.state === 'closed' && closeReason === 'campaign_ended' && layerConfig !== null;
  const endedOnOwnDeadline =
    campaignEnded && layerConfig?.ends_with == null && layerConfig?.deadline_at != null;
  const endedAbove =
    layerConfig?.ends_with?.campaign_name ?? parentRef?.campaign_name ?? LC.anotherCampaign;
  const leaveFromLine = campaignEnded && !endedOnOwnDeadline && where?.can_leave === true;
  const under = structureQuery.data?.under ?? null;
  const recipientsEditable =
    contentEditable &&
    editorRank &&
    !linked &&
    privateQuery.isSuccess &&
    privateQuery.data.stats?.donation_count === 0;
  // A closed row is no longer final (§FC-15): when nothing else locks the list, Reopen is the way
  // back to editing it. A donation still locks it for good, which the picker's own reason says.
  const closedDonationCount =
    row.state === 'closed' && canReopen && privateQuery.isSuccess
      ? privateQuery.data.stats?.donation_count
      : undefined;
  // FCM-WEB28: on a Givmo takedown the picker's own donation lock speaks for a funded row (the
  // portal's rule); an unfunded one says why it is locked.
  const takenDownFunded =
    privateQuery.isSuccess && (privateQuery.data.stats?.donation_count ?? 0) > 0;
  const frozenDeactivatedWayBack = frozenDeactivated && endingAllowed.reactivate;
  const recipientReason = takenDown
    ? takenDownFunded
      ? undefined
      : LIFECYCLE_COPY.takenDownDetails
    : !contentEditable
      ? frozenDeactivatedWayBack && privateQuery.isSuccess
        ? // A donation locks the list for good, which the picker's own line says.
          (privateQuery.data.stats?.donation_count ?? 0) > 0
          ? undefined
          : LIFECYCLE_COPY.frozenDeactivatedRecipients
        : closedDonationCount === 0
          ? 'Reopen this fundraiser to edit its recipients.'
          : closedDonationCount != null && closedDonationCount > 0
            ? undefined
            : 'This fundraiser’s recipients can no longer be edited.'
      : !editorRank
        ? 'Recipient changes aren’t available for this fundraiser.'
        : linked && (privateQuery.data?.stats?.donation_count ?? 0) === 0
          ? LC.linkedRecipients
          : undefined;
  const giving = config.links.donationsGive?.(row.id) ?? null;
  const coverUrl = row.image_url
    ? `${row.image_url}${row.image_url.includes('?') ? '&' : '?'}v=${encodeURIComponent(row.updated_at ?? '')}`
    : null;
  // FCM-WEB28 (option C, ruled 2026-10-05): every way back sits in its state line — Reactivate on a
  // deactivated page, Reopen on a closed one, in the module's own words — and the header keeps only
  // the badge and the forward acts (Publish, Close).
  const stateLine = takenDown ? (
    <StateNotice icon={ShieldAlert} testId="fundraiser-takedown-notice" action={null}>
      {LIFECYCLE_COPY.takedownLead}{' '}
      <a
        href={takedownQuestionHref(row)}
        className={cn('font-w600 text-brand-700 underline-offset-2 hover:underline', INLINE_HIT)}
        {...testIdProp('fundraiser-takedown-support')}
      >
        {LIFECYCLE_COPY.takedownLink}
      </a>{' '}
      {LIFECYCLE_COPY.takedownTail}
    </StateNotice>
  ) : row.state === 'deactivated' ? (
    <StateNotice
      icon={EyeOff}
      testId="fundraiser-deactivated-notice"
      action={
        endingAllowed.reactivate ? (
          <Button
            appearance="saas"
            tone="secondary"
            className={PHONE_HIT}
            loading={ending.isPending && ending.variables === 'reactivate'}
            disabled={busy || dirty}
            onClick={() => ending.mutate('reactivate')}
            testId="fundraiser-reactivate"
          >
            {LIFECYCLE_COPY.reactivate}
          </Button>
        ) : null
      }
    >
      {LIFECYCLE_COPY.deactivatedBanner}
    </StateNotice>
  ) : campaignEnded ? (
    // FCM-WEB25 (FCM-BE35): ended by a deadline — its own (frame 12, the way back is the deadline) or one
    // above it (12b, no way back here; 07c, the parent can never come back, so Leave is the way back).
    <StateNotice
      icon={CalendarClock}
      testId="fundraiser-ended-notice"
      // The closed note's own id; the e2e charity spec reads it.
      textTestId="fundraiser-readonly-note"
      action={
        endedOnOwnDeadline ? (
          writable && ownerRank ? (
            <Button
              appearance="saas"
              tone="secondary"
              className={PHONE_HIT}
              disabled={dirty || busy}
              onClick={() => navigate(FUNDRAISERS_ROUTES.campaign(row.id))}
              testId="fundraiser-change-deadline"
            >
              {LC.changeDeadline}
            </Button>
          ) : null
        ) : leaveFromLine && writable && ownerRank ? (
          <Button
            appearance="saas"
            tone="secondary"
            className={PHONE_HIT}
            disabled={dirty || busy}
            onClick={() => setLeaveOpen(true)}
            testId="fundraiser-ended-leave"
          >
            {LC.leaveAction}
          </Button>
        ) : null
      }
    >
      {endedOnOwnDeadline
        ? LC.endedOnDeadline(
            formatEndDay(layerConfig?.deadline_at ?? '', layerConfig?.deadline_time_zone),
          )
        : leaveFromLine
          ? LC.endedWithGone(endedAbove)
          : LC.endedWith(endedAbove)}
    </StateNotice>
  ) : draftEnded ? (
    // FCM-BE35: a draft under an ended campaign, in the server's own sentence; nothing brings it back
    // here (it publishes only if that campaign reopens).
    <StateNotice icon={CalendarClock} testId="fundraiser-chain-ended-notice" action={null}>
      {SERVER_WORDS.partOfEnded}
    </StateNotice>
  ) : row.state === 'closed' ? (
    <StateNotice
      icon={Lock}
      testId="fundraiser-closed-notice"
      // The closed note's own id since FCM-WEB17; the e2e charity spec reads it.
      textTestId="fundraiser-readonly-note"
      action={
        canReopen ? (
          <Button
            appearance="saas"
            tone="secondary"
            className={PHONE_HIT}
            disabled={dirty || busy}
            onClick={() => setDialog('reopen')}
            testId="fundraiser-reopen"
          >
            Reopen fundraiser
          </Button>
        ) : null
      }
    >
      {/* It promises an edit only where Reopen is offered. A refused Reopen's reason is the
          server's, the one place this page learns why a fundraiser cannot be reopened. */}
      {notReopenable ??
        (canReopen
          ? 'This fundraiser is closed. Reopen it to edit its name, story or goal.'
          : 'This fundraiser is closed.')}
    </StateNotice>
  ) : null;
  // FCM-WEB28: the foot of the Details view — Delete draft, Deactivate, the funded "Need it taken
  // down?" card, or Delete forever, each exactly when its hint says so (rulings Q2, Q4 and Q9).
  const foot = endingAllowed.deleteDraft ? (
    <FootCard
      title={LIFECYCLE_COPY.deleteDraftCardTitle}
      body={LIFECYCLE_COPY.deleteDraftCardBody}
      testId="fundraiser-delete-draft-card"
      action={
        <FootButton
          label={LIFECYCLE_COPY.deleteDraftConfirm}
          disabled={busy || dirty}
          onClick={() => setDialog('deleteDraft')}
          testId="fundraiser-delete-draft"
        />
      }
    />
  ) : endingAllowed.deactivate ? (
    <FootCard
      title={LIFECYCLE_COPY.deactivateCardTitle}
      body={LIFECYCLE_COPY.deactivateCardBody}
      testId="fundraiser-deactivate-card"
      action={
        <FootButton
          label={LIFECYCLE_COPY.deactivateConfirm}
          disabled={busy || dirty}
          onClick={() => setDialog('deactivate')}
          testId="fundraiser-deactivate"
        />
      }
    />
  ) : writable && keptBy !== null ? (
    // FCM-BE31's fold, option A (FCM-BE88 for a Deactivated top): never left with no visible way out.
    <FootCard
      title={
        keptBy === 'draft'
          ? LIFECYCLE_COPY.deleteDraftCardTitle
          : LIFECYCLE_COPY.deleteForeverCardTitle
      }
      body={keptBy === 'draft' ? LC.draftTopFootBody : LC.deactivatedTopFootBody}
      testId="fundraiser-kept-card"
      action={
        <FootButton
          label={LC.seeLayers}
          disabled={busy}
          onClick={() => navigate(FUNDRAISERS_ROUTES.layers(row.id))}
          testId="fundraiser-kept-layers"
        />
      }
    />
  ) : writable && parentRef !== null && where?.can_leave !== true ? (
    // No walk-out (Q5): why an attached layer offers no Deactivate or Delete — the ruled "Need it taken
    // down?" question, answered in the server's own words (frame 09).
    <FootCard
      title={LIFECYCLE_COPY.fundedTitle}
      body={SERVER_WORDS.attached(parentRef.campaign_name)}
      testId="fundraiser-attached-card"
      action={null}
    />
  ) : writable && endings?.contactSupport === true ? (
    <FootCard
      title={LIFECYCLE_COPY.fundedTitle}
      body={LIFECYCLE_COPY.fundedBody}
      testId="fundraiser-takedown-help"
      action={
        <a
          href={takedownSupportHref(row)}
          className={cn(
            saasButtonVariants({ tone: 'secondary', size: 'md' }),
            'relative shrink-0 self-start sm:self-auto',
            PHONE_HIT,
          )}
          {...testIdProp('fundraiser-contact-support')}
        >
          {LIFECYCLE_COPY.fundedAction}
        </a>
      }
    />
  ) : endingAllowed.deleteForever ? (
    <FootCard
      title={LIFECYCLE_COPY.deleteForeverCardTitle}
      body={LIFECYCLE_COPY.deleteForeverCardBody}
      testId="fundraiser-delete-forever-card"
      action={
        <FootButton
          label={LIFECYCLE_COPY.deleteForeverCardTitle}
          disabled={busy || dirty}
          onClick={() => setDialog('deleteForever')}
          testId="fundraiser-delete-forever"
        />
      }
    />
  ) : null;
  return (
    <FundraisersPageShell testId="page-fundraiser-editor">
      <BackButton onClick={() => navigate(FUNDRAISERS_ROUTES.list)} />
      <PageHeader
        title={row.name}
        actions={
          // FCM-WEB35's production fix 15: the row keeps a button's height, so the state badge sits on the
          // title's line with no act beside it too (a closed page, someone who can't close it).
          <div className="flex min-h-36 flex-wrap items-center gap-12 max-lg:min-h-44">
            <Badge tone={badge.tone} testId="fundraiser-state-badge">
              {badge.label}
            </Badge>
            {row.state === 'draft' && writable && !draftEnded ? (
              <Button
                appearance="saas"
                testId="fundraiser-publish"
                disabled={!canPublish || dirty || busy}
                onClick={() => setDialog('publish')}
              >
                Publish fundraiser
              </Button>
            ) : canClose ? (
              <Button
                appearance="saas"
                tone="secondary"
                testId="fundraiser-close"
                disabled={dirty || busy}
                onClick={() => setDialog('close')}
              >
                {/* FCM-WEB35's production fix 14: a game's page calls itself a game's page, one name for
                    one thing, and never reads as the game ending. */}
                {isGame ? 'Close game page' : 'Close fundraiser'}
              </Button>
            ) : null}
            {boardAskServed && writable && (
              <Button
                appearance="saas"
                tone="secondary"
                testId="fundraiser-board-ask"
                onClick={() => navigate(FUNDRAISERS_ROUTES.boardAsk(row.id))}
              >
                {BOARD_ASK_COPY.create}
              </Button>
            )}
          </div>
        }
      />
      {stateLine ??
        (!contentEditable && (
          <p
            role="status"
            className="text-s13 text-text-secondary"
            data-testid="fundraiser-readonly-note"
          >
            This fundraiser is read-only.
          </p>
        ))}
      {/* The shared content row carries the tab inset for BOTH columns; panels add no second inset. */}
      <Tabs.Root
        appearance="saas"
        value={view}
        onValueChange={(value) =>
          navigate(
            value === 'donors'
              ? FUNDRAISERS_ROUTES.donors(row.id)
              : value === 'emails'
                ? FUNDRAISERS_ROUTES.emails(row.id)
                : value === 'social'
                  ? FUNDRAISERS_ROUTES.social(row.id)
                  : value === 'messages'
                    ? FUNDRAISERS_ROUTES.messages(row.id)
                    : value === 'bonus-pledges'
                      ? FUNDRAISERS_ROUTES.bonusPledges(row.id)
                      : value === 'campaign'
                        ? FUNDRAISERS_ROUTES.campaign(row.id)
                        : value === 'layers'
                          ? FUNDRAISERS_ROUTES.layers(row.id)
                          : FUNDRAISERS_ROUTES.editor(row.id),
          )
        }
        className={`${columns} gap-y-20`}
      >
        <Tabs.List
          aria-label="Manage fundraiser"
          className={socialBeside ? 'min-w-0' : 'min-w-0 xl:col-start-1 xl:row-start-1'}
        >
          <Tabs.Tab value="details" testId="fundraiser-details-tab">
            Details
          </Tabs.Tab>
          {/* Second, on every fundraiser it lights, with no count (D-FCM-55's decision 1). */}
          {donorsServe && (
            <Tabs.Tab value="donors" testId="fundraiser-donors-tab">
              {DONORS_COPY.tab}
            </Tabs.Tab>
          )}
          {/* After Donors (the campaign-structure SPEC's order: Details · Page · Donors · Emails · Social). */}
          {(emailsServe || (view === 'emails' && !emailsUnavailable)) && (
            <Tabs.Tab value="emails" testId="fundraiser-emails-tab">
              {EMAILS_COPY.tab}
            </Tabs.Tab>
          )}
          {/* EMC-WEB5: beside Emails (the campaign-structure SPEC's order), where Social serves. */}
          {(socialServe || (view === 'social' && !socialDark)) && (
            <Tabs.Tab value="social" testId="fundraiser-social-tab">
              {CAMPAIGN_SOCIAL_COPY.tab}
            </Tabs.Tab>
          )}
          {/* After Details and Donors (D-FCM-64's decision 1), and after Emails where that tab shows. */}
          {messagesServe && (
            <Tabs.Tab value="messages" testId="fundraiser-messages-tab">
              {MESSAGES_COPY.tab}
            </Tabs.Tab>
          )}
          {/* FCM-WEB35 (decision 1): on a game, after Details, Donors, Emails and Messages (their ruled
              places), before the campaign's settings and structure. */}
          {(isGame || view === 'bonus-pledges') && (
            <Tabs.Tab value="bonus-pledges" testId="fundraiser-bonus-pledges-tab">
              {BONUS_COPY.tab}
            </Tabs.Tab>
          )}
          {(campaignLoaded || upgraded || view === 'campaign') && (
            <Tabs.Tab value="campaign" testId={`p2p-campaign-open-${row.id}`}>
              {/* FCM-WEB33 (decision 1): the people's requests waiting for an answer. */}
              <span className="inline-flex items-center gap-6">
                Campaign
                <TabCount
                  count={umbrella.data?.pendingRequests?.people}
                  testId="campaign-tab-requests-count"
                />
              </span>
            </Tabs.Tab>
          )}
          {/* Only where it serves; on its own route it shows while the overview is still answering. */}
          {((layersServe && parentQuery.isSuccess) ||
            (view === 'layers' && !layersUnavailable)) && (
            <Tabs.Tab value="layers" testId="fundraiser-layers-tab">
              {/* FCM-WEB33 (decision 1): the campaigns' requests waiting for an answer. */}
              <span className="inline-flex items-center gap-6">
                {LC.tab}
                <TabCount
                  count={umbrella.data?.pendingRequests?.campaigns}
                  testId="layers-tab-requests-count"
                />
              </span>
            </Tabs.Tab>
          )}
        </Tabs.List>
        <div className={`${columns} col-span-full pt-16 xl:row-start-2`}>
          <div className="min-w-0">
            <Tabs.Panel value="details" focusable={false} className="flex flex-col gap-20 pt-0">
              <FundraiserFormCard
                fundraiser={row}
                canEditContent={contentEditable}
                canChangeStatistics={canChangeStatistics}
                partOf={parentRef?.campaign_name ?? null}
                // Every read-only page is the record, label over value (rulings Q5 and Q7; option C).
                // A takedown, and a deactivated page a Close froze, say why in the card; a closed
                // page's state line does.
                record={
                  contentEditable
                    ? undefined
                    : takenDown
                      ? LIFECYCLE_COPY.takenDownDetails
                      : frozenDeactivatedWayBack
                        ? LIFECYCLE_COPY.frozenDeactivatedDetails
                        : null
                }
                coverUrl={coverUrl}
                onDirtyChange={setDirty}
                onSaved={refresh}
                onStale={() => void refresh()}
                cover={
                  <FundraiserCover
                    fundraiserId={row.id}
                    imageUrl={coverUrl}
                    readOnly={!contentEditable || !editorRank}
                    busy={busy}
                    onUploaded={refresh}
                  />
                }
              />
              {config.flavor.recipientMode === 'chosen' && (
                <Card flush testId="fundraiser-recipients-card">
                  <CardBody>
                    {wrapper ? (
                      // FCM-WEB25 (frame 13): its pages and layers pick; donors give through them.
                      <div
                        className="flex flex-col gap-8"
                        data-testid="fundraiser-wrapper-recipients"
                      >
                        <span className="text-s13 font-w600 text-text-strong">Recipients</span>
                        <p className="text-s14 leading-h15 text-text-strong">
                          {LC.wrapperRecipients}
                        </p>
                        <p className="text-s12 text-text-secondary">{LC.wrapperWhere}</p>
                        {row.state === 'active' && (
                          <a
                            href={row.public_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="relative self-start text-s14 font-w600 text-brand-700 underline-offset-2 after:absolute after:inset-x-0 after:-inset-y-4 hover:underline max-lg:after:-inset-y-12"
                            {...testIdProp('fundraiser-wrapper-breakdown')}
                          >
                            {LC.wrapperBreakdown}
                          </a>
                        )}
                      </div>
                    ) : privateQuery.isSuccess ? (
                      <RecipientPicker
                        fundraiser={privateQuery.data}
                        disabled={!recipientsEditable || busy}
                        disabledReason={recipientReason}
                        onChanged={refresh}
                      />
                    ) : privateQuery.isError ? (
                      <EmptyState
                        testId="fundraiser-recipients-error"
                        title="Couldn’t load recipients"
                        action={
                          <Button
                            appearance="saas"
                            tone="secondary"
                            testId="fundraiser-recipients-retry"
                            onClick={() => void privateQuery.refetch()}
                          >
                            Try again
                          </Button>
                        }
                      />
                    ) : (
                      <p role="status" className="text-s13 text-text-secondary">
                        Loading recipients…
                      </p>
                    )}
                  </CardBody>
                </Card>
              )}
              {row.designations != null &&
                (contentEditable ? (
                  <DesignationsEditor
                    orgRef={config.orgRef}
                    fundraiserId={row.id}
                    designations={row.designations}
                    onSaved={refresh}
                    disabled={busy}
                  />
                ) : (
                  // The record carries on into the designations: their labels as written.
                  <DesignationsRecord designations={row.designations} />
                ))}
              {canUpgrade && (
                <Card flush testId="fundraiser-upgrade-card">
                  <CardHeader
                    title="Bring supporters into a Campaign"
                    actions={<HelpButton onClick={() => setHelp(true)} />}
                  />
                  <CardBody className="flex flex-col gap-16">
                    <p className="text-s14 text-text-secondary">{CAMPAIGN_EXPLAINER}</p>
                    <Button
                      appearance="saas"
                      tone="secondary"
                      className="self-start"
                      testId="p2p-enable-button"
                      disabled={dirty || busy}
                      onClick={() => setDialog('upgrade')}
                    >
                      Upgrade to Campaign
                    </Button>
                  </CardBody>
                </Card>
              )}
            </Tabs.Panel>
            <Tabs.Panel value="donors" focusable={false} className="pt-0">
              {/* Its own address while the detail lights no list: the tab is hidden, and here says so. */}
              {view === 'donors' &&
                (donorsServe ? <DonorsTab fundraiserId={row.id} /> : <DonorsUnavailable />)}
            </Tabs.Panel>
            <Tabs.Panel value="emails" focusable={false} className="pt-0">
              {view === 'emails' &&
                (emailsUnavailable ? <EmailsUnavailable /> : <EmailsTab fundraiserId={row.id} />)}
            </Tabs.Panel>
            <Tabs.Panel value="social" focusable={false} className="pt-0">
              {view === 'social' &&
                (socialDark ? (
                  <CampaignSocialUnavailable />
                ) : (
                  <CampaignSocialTab fundraiserId={row.id} live={row.state === 'active'} />
                ))}
            </Tabs.Panel>
            <Tabs.Panel value="messages" focusable={false} className="pt-0">
              {/* Its own address while the detail lights no composer: the tab is hidden, and here says
                  so, as the Donors tab's address does. */}
              {view === 'messages' &&
                (messagesServe ? <MessagesTab fundraiserId={row.id} /> : <MessagesUnavailable />)}
            </Tabs.Panel>
            <Tabs.Panel value="bonus-pledges" focusable={false} className="pt-0">
              {view === 'bonus-pledges' && (
                <BonusPledgesTab
                  row={row}
                  canManage={ownerRank && writable}
                  enabled={scoped && layersOn}
                />
              )}
            </Tabs.Panel>
            <Tabs.Panel value="campaign" focusable={false} className="pt-0">
              <P2PCampaignPage
                campaignId={row.id}
                canManage={ownerRank}
                enabled={scoped}
                facts={{
                  name: row.name,
                  state: row.state,
                  closeReason,
                  goal: row.goal_amount ?? null,
                  charities: privateQuery.isSuccess
                    ? currentRecipients(privateQuery.data).map((item) => item.name)
                    : [],
                  ownDonations,
                  chainEnded: draftEnded,
                }}
                onSaved={refresh}
              />
            </Tabs.Panel>
            <Tabs.Panel value="layers" focusable={false} className="pt-0">
              {view === 'layers' && (
                <LayersTab
                  row={row}
                  canManage={ownerRank && writable}
                  enabled={scoped && layersServe}
                  unavailable={layersUnavailable}
                  endedBy={draftEnded ? endedAbove : null}
                  keptBy={keptBy}
                  leaveInStateLine={leaveFromLine}
                  selfCharities={
                    privateQuery.isSuccess
                      ? currentRecipients(privateQuery.data).map((item) => item.name)
                      : []
                  }
                  onChanged={refresh}
                />
              )}
            </Tabs.Panel>
          </div>
          {/* Kept mounted beside the Social tab's posts (frame 40), so its reads stay warm for the
              other tabs. */}
          <aside
            className={cn('flex min-w-0 flex-col gap-20', socialBeside && 'hidden')}
            data-testid="fundraiser-aside"
          >
            <Card flush testId="fundraiser-preview-card">
              <CardHeader
                title={upgraded ? (isGame ? 'Game raised' : 'Campaign raised') : 'Progress'}
                actions={upgraded ? <HelpButton onClick={() => setHelp(true)} /> : undefined}
              />
              <CardBody className="flex flex-col gap-16">
                <GoalThermometer
                  raised={row.stats.total_raised}
                  goal={row.goal_amount}
                  percent={row.goal_progress_percent}
                  testId="fundraiser-thermometer"
                />
                {(upgraded || row.stats.donation_count > 0) && (
                  <dl className="flex flex-col gap-8 border-t border-border-subtle pt-16 text-s13">
                    {pagesRaised !== null && row.stats.direct_raised != null
                      ? // EMC-WEB2 (walkthrough screen 20): the total, split as the server split it:
                        // the campaign page's own and its fundraiser pages', both served figures.
                        // FCM-WEB35's production fixes 14 and 16: a game's page names itself, and splits only
                        // what it took itself: "$0" on a page that takes no gift says nothing.
                        row.stats.total_raised !== '0.00' &&
                        !(isGame && row.stats.direct_raised === '0.00') && (
                          <>
                            <div
                              className="flex justify-between gap-12"
                              data-testid="fundraiser-direct-raised"
                            >
                              <dt className="text-text-secondary">
                                {isGame ? 'On the game page' : 'On the campaign page'}
                              </dt>
                              <dd className="tabular-nums text-text-strong">
                                {formatFundraiserMoney(row.stats.direct_raised)}
                              </dd>
                            </div>
                            <div
                              className="flex justify-between gap-12"
                              data-testid="fundraiser-pages-raised"
                            >
                              <dt className="text-text-secondary">On fundraiser pages</dt>
                              <dd className="tabular-nums text-text-strong">
                                {formatFundraiserMoney(pagesRaised)}
                              </dd>
                            </div>
                          </>
                        )
                      : row.stats.direct_raised != null &&
                        row.stats.direct_raised !== row.stats.total_raised && (
                          <div
                            className="flex justify-between gap-12"
                            data-testid="fundraiser-direct-raised"
                          >
                            <dt className="text-text-secondary">This page raised</dt>
                            <dd className="tabular-nums text-text-strong">
                              {formatFundraiserMoney(row.stats.direct_raised)}
                            </dd>
                          </div>
                        )}
                    <div className="flex justify-between gap-12">
                      <dt className="text-text-secondary">Donations</dt>
                      <dd className="tabular-nums text-text-strong">{row.stats.donation_count}</dd>
                    </div>
                  </dl>
                )}
              </CardBody>
            </Card>
            {row.state === 'active' ? (
              // CPB-WEB3: the share kit, the organization's own (its canonical link, words in its
              // voice, its library's current graphics). While the kit is switched off it is the
              // route miss, and the share panel it replaces stands in.
              <ShareKit
                entityType="fundraiser"
                entityId={row.id}
                orgRef={config.orgRef}
                noun={isGame ? 'game' : undefined}
                onCustomizeQr={
                  config.qrDesign ? () => navigate(FUNDRAISERS_ROUTES.qr(row.id)) : undefined
                }
                fallback={
                  <SharePanel
                    orgRef={config.orgRef}
                    fundraiserId={row.id}
                    noun={isGame ? 'game' : undefined}
                    onCustomize={
                      config.qrDesign ? () => navigate(FUNDRAISERS_ROUTES.qr(row.id)) : undefined
                    }
                  />
                }
                // EMC-BE23: a campaign's card shows only its picks, so with none it says where they're
                // chosen (the Campaign tab's card, scrolled to there, or opened from another tab), or, to
                // a reader who can't pick them, who does.
                noGraphics={
                  upgraded ? (
                    <ShareGraphicsHint
                      {...(ownerRank
                        ? {
                            onPick: () => {
                              const card = document.getElementById(SHARE_GRAPHICS_CARD_ID);
                              if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              else navigate(FUNDRAISERS_ROUTES.campaign(row.id));
                            },
                          }
                        : {})}
                    />
                  ) : undefined
                }
              />
            ) : row.state === 'draft' && !draftEnded ? (
              <p className="text-s13 text-text-secondary">
                {dirty
                  ? 'Save your changes before publishing.'
                  : hasRecipients
                    ? 'Publish to make this fundraiser page public.'
                    : 'Add recipients, then publish to make this fundraiser page public.'}
              </p>
            ) : null}
            {row.state === 'active' && giving && !wrapper && (
              <a
                href={giving.href}
                // FCM-WEB29: where the host serves Donations in this console, a plain click is an
                // in-app hop through its navigation (asking first while edits are unsaved), never a
                // reload; every other gesture, and an external destination, is the anchor's own.
                onClick={
                  giving.inApp
                    ? (event) => {
                        if (!shouldInterceptClick(event)) return;
                        event.preventDefault();
                        navigate(giving.href);
                      }
                    : undefined
                }
                data-testid="fundraiser-give-link"
                className="inline-flex min-h-44 items-center text-s14 font-w600 text-brand-700 hover:underline"
              >
                Donate to this fundraiser
              </a>
            )}
          </aside>
          {/* FCM-WEB28 (ruling 1): the foot card closes the Details view — under the main column at
              desktop widths, and below the side column on a phone, so it is the page's last card at
              every width — and never shows on the Campaign tab. */}
          {view === 'details' && foot !== null && (
            // The row gap is 24px; the cards above it stand 20px apart, so the foot does too.
            <div
              className="-mt-4 min-w-0 xl:col-start-1 xl:row-start-2"
              data-testid="fundraiser-foot"
            >
              {foot}
            </div>
          )}
        </div>
      </Tabs.Root>
      <ConfirmDialog
        open={dialog === 'publish'}
        onOpenChange={(open) => {
          if (!open && !lifecycle.isPending) setDialog(null);
        }}
        title="Publish this fundraiser?"
        body="Publishing makes your fundraiser page public, so anyone with the link can donate. You can close it later, but you can’t unpublish it."
        confirmLabel="Publish"
        testId="fundraiser-publish-dialog"
        confirmTestId="fundraiser-publish-confirm"
        busy={lifecycle.isPending}
        disabled={!canPublish || dirty}
        onConfirm={() => lifecycle.mutate('publish')}
      />
      <ConfirmDialog
        open={dialog === 'close'}
        onOpenChange={(open) => {
          if (!open && !lifecycle.isPending) setDialog(null);
        }}
        title={isGame ? 'Close this game page?' : 'Close this fundraiser?'}
        body={`${row.show_statistics === false ? CLOSE_COPY_TOTALS_HIDDEN : CLOSE_COPY}${
          under && under.layers > 0 ? ` ${LC.closeCascade(under.run_by_others)}` : ''
        }`}
        confirmLabel={isGame ? 'Close game page' : 'Close fundraiser'}
        // Reversible since §FC-15, so the primary confirm, not the destructive one; the employer
        // portal's Close (FCM-WEB18) confirms the same way.
        testId="fundraiser-close-dialog"
        confirmTestId="fundraiser-close-confirm"
        busy={lifecycle.isPending}
        disabled={!canClose || dirty}
        onConfirm={() => lifecycle.mutate('close')}
      />
      <ConfirmDialog
        open={dialog === 'reopen'}
        onOpenChange={(open) => {
          if (!open && !lifecycle.isPending) setDialog(null);
        }}
        title="Reopen this fundraiser?"
        body={`${REOPEN_COPY}${
          under && under.layers > 0 ? ` ${LC.reopenCascade(under.run_by_others)}` : ''
        }`}
        confirmLabel="Reopen"
        testId="fundraiser-reopen-dialog"
        confirmTestId="fundraiser-reopen-confirm"
        busy={lifecycle.isPending}
        disabled={!canReopen || dirty}
        onConfirm={() => lifecycle.mutate('reopen')}
      />
      {/* FCM-WEB28: the take-down and delete confirms, in the module's own dialog with the portal's
          ruled words (ruling 6: no fundraiser name), destructive. */}
      <ConfirmDialog
        open={dialog === 'deactivate'}
        onOpenChange={(open) => {
          if (!open && !ending.isPending) setDialog(null);
        }}
        title={LIFECYCLE_COPY.deactivateTitle}
        body={LIFECYCLE_COPY.deactivateBody}
        confirmLabel={LIFECYCLE_COPY.deactivateConfirm}
        testId="fundraiser-deactivate-dialog"
        confirmTestId="fundraiser-deactivate-confirm"
        tone="danger"
        busy={ending.isPending}
        disabled={!endingAllowed.deactivate || dirty}
        onConfirm={() => ending.mutate('deactivate')}
      />
      <ConfirmDialog
        open={dialog === 'deleteDraft'}
        onOpenChange={(open) => {
          if (!open && !ending.isPending) setDialog(null);
        }}
        title={LIFECYCLE_COPY.deleteDraftTitle}
        body={LIFECYCLE_COPY.cantBeUndone}
        confirmLabel={LIFECYCLE_COPY.deleteDraftConfirm}
        testId="fundraiser-delete-draft-dialog"
        confirmTestId="fundraiser-delete-draft-confirm"
        tone="danger"
        busy={ending.isPending}
        disabled={!endingAllowed.deleteDraft || dirty}
        onConfirm={() => ending.mutate('deleteDraft')}
      />
      <ConfirmDialog
        open={dialog === 'deleteForever'}
        onOpenChange={(open) => {
          if (!open && !ending.isPending) setDialog(null);
        }}
        title={LIFECYCLE_COPY.deleteForeverTitle}
        body={LIFECYCLE_COPY.cantBeUndone}
        confirmLabel={LIFECYCLE_COPY.deleteForeverConfirm}
        testId="fundraiser-delete-forever-dialog"
        confirmTestId="fundraiser-delete-forever-confirm"
        tone="danger"
        busy={ending.isPending}
        disabled={!endingAllowed.deleteForever || dirty}
        onConfirm={() => ending.mutate('deleteForever')}
      />
      <ConfirmDialog
        open={dialog === 'upgrade' && canUpgrade}
        onOpenChange={(open) => {
          if (!open && !lifecycle.isPending) setDialog(null);
        }}
        title="Upgrade to Campaign?"
        body={PUBLIC_ENABLE_COPY}
        confirmLabel="Upgrade to Campaign"
        testId="p2p-enable-dialog"
        confirmTestId="p2p-enable-confirm"
        cancelTestId="p2p-enable-cancel"
        busy={lifecycle.isPending}
        disabled={!canUpgrade || dirty}
        onConfirm={() => lifecycle.mutate('upgrade')}
      />
      {leaveFromLine && (
        <LeaveDialogHost
          campaignId={row.id}
          parentName={endedAbove}
          open={leaveOpen}
          onClose={() => setLeaveOpen(false)}
          onDone={refresh}
        />
      )}
      <ConfirmDialog
        open={help}
        onOpenChange={setHelp}
        title="What is a Campaign?"
        body={CAMPAIGN_EXPLAINER}
        confirmLabel="Got it"
        testId="fundraiser-explainer-dialog"
        confirmTestId="fundraiser-explainer-close"
        onConfirm={() => setHelp(false)}
        informational
      />
      <ConfirmDialog
        open={leave !== null}
        onOpenChange={(open) => {
          if (!open) setLeave(null);
        }}
        title="Discard unsaved changes?"
        body="Your saved fundraiser will remain unchanged."
        confirmLabel="Discard changes"
        testId="fundraiser-discard-dialog"
        confirmTestId="fundraiser-discard-confirm"
        tone="danger"
        onConfirm={() => {
          if (leave) {
            setDirty(false);
            config.onNavigate(leave);
            setLeave(null);
          }
        }}
      />
    </FundraisersPageShell>
  );
}

function FundraiserFormCard({
  fundraiser,
  canEditContent,
  canChangeStatistics,
  partOf = null,
  cover,
  record,
  coverUrl,
  onSaved,
  onStale,
  onDirtyChange,
}: {
  fundraiser?: OrgFundraiserOut;
  canEditContent: boolean;
  canChangeStatistics: boolean;
  /** F15 (FCM-WEB25): the campaign this one is part of, for the totals switch's helper. */
  partOf?: string | null;
  /** The editable form's cover control; the record shows the cover itself (`coverUrl`). */
  cover?: ReactNode;
  /**
   * FCM-WEB28: show the content as the read-only record, label over value (rulings Q5 and Q7) —
   * `undefined` keeps the form; a string heads the record with that line; `null` shows it bare.
   */
  record?: string | null;
  coverUrl?: string | null;
  onSaved: (row: OrgFundraiserOut) => void | Promise<unknown>;
  /** A save the server refused with a 409: the fundraiser moved under the page, so it re-reads. */
  onStale?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const config = useFundraisersModule();
  const live = useLivePage();
  const [baseline, setBaseline] = useState(fundraiser);
  const [values, setValues] = useState<FundraiserFormValues>(() =>
    fundraiser ? fundraiserToForm(fundraiser) : emptyForm(),
  );
  const [touched, setTouched] = useState(false);
  const parsed = fundraiserSchema.safeParse(values);
  // FCM-WEB6 — the organization contact offer (D-FCM-15 ◆B: the draft editor, before publish) rides
  // this card's one Save. It appears only while the read carries `contact_offer` (a company's row,
  // the backend switch on); a charity's row carries none, so nothing here branches on class. Like
  // `show_statistics` it is a disclosure preference the server still accepts on a closed row, so it
  // follows that switch's rule rather than the content's.
  const offer = baseline ? contactOfferFromOrgRead(baseline) : null;
  const ownEmail = config.viewerEmail?.trim() || null;
  const [offerShared, setOfferShared] = useState(offer?.shared ?? false);
  const [offerEmail, setOfferEmail] = useState(offer?.email ?? ownEmail ?? '');
  const [offerError, setOfferError] = useState<string | null>(null);
  const offerRule = {
    ownEmail,
    offeredEmail: offer?.email ?? null,
    domain: offer?.domain ?? null,
  };
  // Sharing OFF never holds the Save back; sharing ON needs a usable address.
  const offerReady = !offer || orgContactProblem(offerShared, offerEmail, offerRule) === null;
  const offerWrite =
    offer && canChangeStatistics ? orgContactWrite(offerShared, offerEmail, offer) : null;
  const contentPatch = baseline ? buildUpdatePatch(baseline, values) : null;
  const basePatch: OrgFundraiserUpdate | null = canEditContent
    ? contentPatch
    : contentPatch?.show_statistics != null && canChangeStatistics
      ? { show_statistics: contentPatch.show_statistics }
      : null;
  const patch: OrgFundraiserUpdate | null =
    basePatch || offerWrite ? { ...basePatch, ...offerWrite } : null;
  const dirty = fundraiser
    ? patch !== null
    : values.name.trim() !== '' ||
      values.description !== '' ||
      values.goal !== '' ||
      values.story !== '';
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);
  // Refetches from recipient/image/designation mutations must not overwrite
  // still-unsaved content. Once clean, adopt the latest server response.
  useEffect(() => {
    if (fundraiser && fundraiser !== baseline && !dirty) {
      setBaseline(fundraiser);
      setValues(fundraiserToForm(fundraiser));
      const next = contactOfferFromOrgRead(fundraiser);
      setOfferShared(next?.shared ?? false);
      setOfferEmail(next?.email ?? ownEmail ?? '');
    }
  }, [fundraiser, baseline, dirty, ownEmail]);
  const save = useMutation({
    mutationFn: async () => {
      if (
        !config.orgRef ||
        !mayWriteFundraisers(config) ||
        (!canEditContent && !canChangeStatistics)
      )
        throw new Error('Fundraiser changes are not available.');
      if (!fundraiser) {
        const { data } = await sdk.orgFundraisersCreateOrgFundraiser({
          path: { org_ref: config.orgRef },
          body: buildCreateBody(values),
          throwOnError: true,
        });
        return requireOrgFundraiser(data, config.orgRef);
      }
      if (!patch) return fundraiser;
      const { data } = await sdk.orgFundraisersUpdateOrgFundraiser({
        path: { org_ref: config.orgRef, fundraiser_id: fundraiser.id },
        body: patch,
        throwOnError: true,
      });
      return requireOrgFundraiser(data, config.orgRef);
    },
    onSuccess: async (row) => {
      if (live()) {
        setBaseline(row);
        setValues(fundraiserToForm(row));
        const next = contactOfferFromOrgRead(row);
        setOfferShared(next?.shared ?? false);
        setOfferEmail(next?.email ?? ownEmail ?? '');
        setOfferError(null);
        onDirtyChange?.(false);
      }
      await onSaved(row);
      if (live()) toast.success(fundraiser ? 'Changes saved' : 'Draft created');
    },
    onError: (error) => {
      if (!live()) return;
      // The server's refusal of the contact address belongs on that field, not in a toast.
      const refusal = orgContactRefusal(error);
      if (refusal !== null) setOfferError(refusal);
      else
        toast.error(
          isGivmoError(error) ? error.message : 'Couldn’t save your fundraiser. Please try again.',
        );
      // FCM-WEB28: a 409 means the page went stale (Givmo took it down, or it was closed, while it
      // was open), so it re-reads and shows what the server holds; the portal's save does the same.
      if (isGivmoError(error) && error.status === 409) onStale?.();
    },
  });
  function set<K extends keyof FundraiserFormValues>(key: K, value: FundraiserFormValues[K]) {
    setValues((previous) => ({ ...previous, [key]: value }));
  }
  const fields = touched && !parsed.success ? parsed.error.flatten().fieldErrors : {};
  const contentDisabled = !canEditContent || save.isPending;
  const asRecord = record !== undefined && fundraiser != null;
  return (
    <Card flush testId="fundraiser-form-card">
      {/* FCM-WEB28: a record that must say why heads itself with the line, in the header strip the
          portal's Details card carries (ruling Q7's line); the form card has no title of its own. */}
      {asRecord && record ? <CardHeader subtitle={record} testId="fundraiser-record-note" /> : null}
      <CardBody>
        <form
          className="flex flex-col gap-20"
          data-testid="fundraiser-form"
          // The card's own checks speak for every field; a native bubble would be a second voice.
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            setTouched(true);
            if (parsed.success && offerReady && !save.isPending && (!fundraiser || patch)) {
              // A new attempt withdraws the last refusal; this one speaks for itself.
              setOfferError(null);
              save.mutate();
            }
          }}
        >
          {asRecord ? (
            <FundraiserRecord fundraiser={fundraiser} coverUrl={coverUrl ?? null} />
          ) : (
            <>
              <TextField
                label="Fundraiser name"
                value={!canEditContent && fundraiser ? fundraiser.name : values.name}
                onChange={(event) => set('name', event.target.value)}
                onBlur={() => setTouched(true)}
                maxLength={100}
                disabled={contentDisabled}
                error={Boolean(fields.name?.[0])}
                errorMessage={fields.name?.[0]}
                testId="fundraiser-name-input"
              />
              <TextField
                label="Goal (USD, optional)"
                value={
                  !canEditContent && fundraiser ? displayGoal(fundraiser.goal_amount) : values.goal
                }
                onChange={(event) => set('goal', event.target.value)}
                onBlur={() => setTouched(true)}
                inputMode="decimal"
                disabled={contentDisabled}
                error={Boolean(fields.goal?.[0])}
                errorMessage={fields.goal?.[0]}
                testId="fundraiser-goal-input"
              />
              {cover}
              <Textarea
                label="Short description (optional)"
                value={
                  !canEditContent && fundraiser
                    ? (fundraiser.description ?? '')
                    : values.description
                }
                onChange={(event) => set('description', event.target.value)}
                onBlur={() => setTouched(true)}
                rows={2}
                maxLength={2000}
                disabled={contentDisabled}
                error={Boolean(fields.description?.[0])}
                errorMessage={fields.description?.[0]}
                testId="fundraiser-description-input"
              />
              <Textarea
                label="Story (optional)"
                value={!canEditContent && fundraiser ? (fundraiser.story ?? '') : values.story}
                onChange={(event) => set('story', event.target.value)}
                onBlur={() => setTouched(true)}
                rows={5}
                maxLength={20000}
                disabled={contentDisabled}
                error={Boolean(fields.story?.[0])}
                errorMessage={fields.story?.[0]}
                testId="fundraiser-story-input"
              />
            </>
          )}
          <div className="flex items-center justify-between gap-16 border-t border-border-subtle pt-20">
            <div>
              <p className="text-s14 font-w600 text-text-strong">Show fundraising totals</p>
              <p className="mt-4 text-s13 text-text-secondary">
                Show the amount raised on the public page.
                {partOf ? ` ${LC.switchAttached(partOf)}` : ''}
              </p>
            </div>
            <Switch
              appearance="saas"
              aria-label="Show fundraising totals"
              checked={values.showStatistics}
              onCheckedChange={(value) => set('showStatistics', value)}
              disabled={!canChangeStatistics || save.isPending}
              testId="fundraiser-show-statistics"
            />
          </div>
          {offer && (
            <OrgContactOffer
              orgName={offer.name ?? config.orgName}
              rule={offerRule}
              shared={offerShared}
              email={offerEmail}
              onSharedChange={(value) => {
                setOfferShared(value);
                setOfferError(null);
              }}
              onEmailChange={(value) => {
                setOfferEmail(value);
                setOfferError(null);
              }}
              serverError={offerError}
              showErrors={touched}
              disabled={!canChangeStatistics || save.isPending}
              headingLevel={3}
              testIdPrefix="fundraiser"
              className="border-t border-border-subtle pt-20"
            />
          )}
          {(canEditContent || canChangeStatistics) && (
            <div className="flex justify-end">
              <Button
                type="submit"
                appearance="saas"
                disabled={
                  !parsed.success || !offerReady || save.isPending || (fundraiser != null && !patch)
                }
                loading={save.isPending}
                testId="fundraiser-save"
              >
                {fundraiser ? 'Save changes' : 'Create draft'}
              </Button>
            </div>
          )}
        </form>
      </CardBody>
    </Card>
  );
}

/**
 * FCM-WEB28: a read-only page's details as the record (rulings Q5 and Q7; option C) — the form's own
 * labels over the values, nothing to edit. An empty value is the muted em dash, heard as "No goal"
 * (ruling Q1) or "None".
 */
function FundraiserRecord({
  fundraiser,
  coverUrl,
}: {
  fundraiser: OrgFundraiserOut;
  coverUrl: string | null;
}) {
  const description = fundraiser.description?.trim() ?? '';
  const story = fundraiser.story?.trim() ?? '';
  return (
    <dl className="flex flex-col gap-16" data-testid="fundraiser-details-record">
      <RecordRow term="Fundraiser name" testId="fundraiser-record-name">
        {fundraiser.name}
      </RecordRow>
      <RecordRow term="Goal" testId="fundraiser-record-goal">
        {fundraiser.goal_amount ? (
          formatFundraiserMoney(fundraiser.goal_amount)
        ) : (
          <EmptyValue spoken="No goal" />
        )}
      </RecordRow>
      <RecordRow term="Cover photo" testId="fundraiser-record-cover">
        {coverUrl ? (
          <img src={coverUrl} alt="Fundraiser cover" className={COVER_FRAME_CLASS} />
        ) : (
          <span className="text-s12 text-text-tertiary" data-testid="fundraiser-cover-none">
            No cover photo.
          </span>
        )}
      </RecordRow>
      <RecordRow term="Short description" testId="fundraiser-record-description">
        {description.length > 0 ? description : <EmptyValue spoken="None" />}
      </RecordRow>
      <RecordRow term="Story" testId="fundraiser-record-story">
        {story.length > 0 ? story : <EmptyValue spoken="None" />}
      </RecordRow>
    </dl>
  );
}

/** FCM-WEB28: a read-only page's designations as a record: the active labels, in order. */
function DesignationsRecord({
  designations,
}: {
  designations: NonNullable<OrgFundraiserOut['designations']>;
}) {
  const active = designations
    .filter((designation) => designation.is_active !== false)
    .sort((a, b) => a.position - b.position);
  return (
    <Card flush testId="fundraiser-designations-record">
      <CardHeader title="Designations" />
      <CardBody>
        {active.length === 0 ? (
          <p className="font-sans text-s16 leading-h15 tracking-givmo text-text-strong">
            <EmptyValue spoken="None" />
          </p>
        ) : (
          <ul className="flex flex-col gap-8">
            {active.map((designation) => (
              <li
                key={designation.id}
                className="font-sans text-s16 leading-h15 tracking-givmo text-text-strong"
              >
                {designation.label}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <div>
      <Button
        appearance="saas"
        tone="ghost"
        size="sm"
        leadingIcon={<Icon icon={ArrowLeft} size={14} aria-hidden />}
        onClick={onClick}
        testId="fundraiser-back"
      >
        Fundraisers
      </Button>
    </div>
  );
}
function HelpButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      appearance="saas"
      tone="ghost"
      size="sm"
      // FCM-WEB35's production fix 19: the card header sets its acts at the top of the title's line, and this
      // button is taller than that line (32 px, 44 below 1024 px), so it sat below the title's centre and held
      // the header open by the difference. It keeps its whole target but takes only the title's 24 px line.
      className="-my-4 max-lg:-my-10"
      aria-label="About campaigns"
      testId="fundraiser-explainer"
      onClick={onClick}
    >
      <Icon icon={HelpCircle} size={16} aria-hidden />
    </Button>
  );
}
